import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useLocation, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { useAuthStore } from "@/store/authStore"
import { resetPassword } from "@/api/auth"
import { toast } from "sonner"

const schema = z.object({
  username: z.string().min(1, "请输入用户名"),
  password: z.string().min(6, "密码至少 6 位"),
})

type FormValues = z.infer<typeof schema>

const resetSchema = z.object({
  username: z.string().min(1, "请输入用户名"),
  email: z.string().email("请输入有效邮箱"),
  newPassword: z.string().min(6, "新密码至少 6 位"),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "两次输入的密码不一致",
  path: ["confirmPassword"],
})

type ResetFormValues = z.infer<typeof resetSchema>

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, loading, error, user, initFromStorage } = useAuthStore()
  const [resetOpen, setResetOpen] = useState(false)
  const [resetSuccess, setResetSuccess] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  })

  const {
    register: registerReset,
    handleSubmit: handleResetSubmit,
    reset: resetForm,
    formState: { errors: resetErrors, isSubmitting: isResetSubmitting },
  } = useForm<ResetFormValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: {
      username: "",
      email: "",
      newPassword: "",
      confirmPassword: "",
    },
  })

  useEffect(() => {
    if (!user) {
      initFromStorage()
    }
  }, [user, initFromStorage])

  useEffect(() => {
    if (user) {
      const state = location.state as { from?: string } | undefined
      navigate(state?.from || "/", { replace: true })
    }
  }, [user, location, navigate])

  const onSubmit = async (values: FormValues) => {
    try {
      await login(values)
    } catch {
    }
  }

  const onResetSubmit = async (values: ResetFormValues) => {
    try {
      await resetPassword({
        username: values.username,
        email: values.email,
        newPassword: values.newPassword,
      })
      toast.success("密码重置成功，请使用新密码登录")
      setResetSuccess(true)
      resetForm()
      setTimeout(() => {
        setResetOpen(false)
        setResetSuccess(false)
      }, 2000)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "重置密码失败")
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full max-w-sm space-y-4 rounded-md border bg-white p-6 shadow-sm"
      >
        <h1 className="text-center text-xl font-semibold">登录</h1>
        <div className="space-y-1">
          <label className="text-sm">用户名</label>
          <Input {...register("username")} />
          {errors.username && (
            <p className="text-xs text-red-500">{errors.username.message}</p>
          )}
        </div>
        <div className="space-y-1">
          <label className="text-sm">密码</label>
          <Input type="password" {...register("password")} />
          {errors.password && (
            <p className="text-xs text-red-500">{errors.password.message}</p>
          )}
        </div>
        {error && <p className="text-xs text-red-500">{error}</p>}
        <Button type="submit" className="mt-2 w-full" disabled={loading}>
          登录
        </Button>
        <div className="flex justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => navigate("/register")}
          >
            去注册
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-sm text-slate-500 hover:text-slate-700"
            onClick={() => setResetOpen(true)}
          >
            忘记密码？
          </Button>
        </div>
      </form>

      <Dialog open={resetOpen} onOpenChange={(v) => {
        setResetOpen(v)
        if (!v) {
          setResetSuccess(false)
          resetForm()
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>重置密码</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleResetSubmit(onResetSubmit)}>
            <p className="text-sm text-slate-500">
              输入注册时的用户名和邮箱，验证通过后即可设置新密码。
            </p>
            <div className="space-y-1">
              <label className="text-sm">用户名</label>
              <Input {...registerReset("username")} placeholder="请输入用户名" />
              {resetErrors.username && (
                <p className="text-xs text-red-500">{resetErrors.username.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <label className="text-sm">注册邮箱</label>
              <Input {...registerReset("email")} placeholder="请输入注册邮箱" />
              {resetErrors.email && (
                <p className="text-xs text-red-500">{resetErrors.email.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <label className="text-sm">新密码</label>
              <Input type="password" {...registerReset("newPassword")} placeholder="至少 6 位" />
              {resetErrors.newPassword && (
                <p className="text-xs text-red-500">{resetErrors.newPassword.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <label className="text-sm">确认新密码</label>
              <Input type="password" {...registerReset("confirmPassword")} placeholder="再次输入新密码" />
              {resetErrors.confirmPassword && (
                <p className="text-xs text-red-500">{resetErrors.confirmPassword.message}</p>
              )}
            </div>
            {resetSuccess && (
              <p className="text-sm text-green-600">密码重置成功！</p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setResetOpen(false)
                  setResetSuccess(false)
                  resetForm()
                }}
              >
                取消
              </Button>
              <Button type="submit" disabled={isResetSubmitting || resetSuccess}>
                确认重置
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
